import { useCallback } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import FormFieldset from '@/components/atoms/Fieldset/templates/FormFieldset';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useIntegrations } from '@/hooks/useIntegrations';
import { cn } from '@/lib/utils';
import { getPermissions } from '@/redux/permissions/selectors';
import { Integration } from '@/shared/types/integration';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';
import { integrationSessionTimeoutSchema } from '@/util/validation/rules/integration-rules';

type Form = { session_timeout?: number };
interface Props {
  integration?: Integration;
}

const SessionTimeoutFieldset = ({ integration }: Props) => {
  const { t } = useTranslation();

  const { updateIntegration, updateStatus } = useIntegrations(
    integration?.desk_id,
    integration?.integration_id
  );

  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'integrations', actions.WRITE, integration?.integration_id)
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isValid },
  } = useForm<Form>({
    mode: 'onChange',
    values: { session_timeout: integration?.config?.session_timeout / 60 },
    resolver: yupResolver<Form, unknown, Form>(integrationSessionTimeoutSchema),
  });

  const onSubmit = useCallback<(form: Form) => void>(
    (form) => {
      const updatedConfig = { ...integration.config };

      // Only set session_timeout if the user provided a value
      if (form?.session_timeout) {
        // Convert minutes to seconds for the API
        updatedConfig.session_timeout = form.session_timeout * 60;
      } else {
        // Remove the field if empty
        delete updatedConfig.session_timeout;
      }

      // TODO: Inform the user what is the default value for each integration.

      updateIntegration({ ...integration, config: updatedConfig });

      // Reset form to clear dirty state after successful update
      reset({
        session_timeout: updatedConfig.session_timeout ? updatedConfig.session_timeout / 60 : null,
      });
    },
    [integration, updateIntegration, reset]
  );

  return (
    <FormFieldset
      title={t('integrations.session_timeout.title')}
      subtitle={t('integrations.session_timeout.subtitle')}
      primaryButton={{
        children: t('common.save'),
        isLoading: updateStatus === 'pending',
        disabled: !isDirty || !isValid,
      }}
      onSubmit={handleSubmit(onSubmit)}
      isLoading={!integration}
    >
      <div className="space-y-2">
        <Label htmlFor="session_timeout" className="text-sm font-medium">
          {t('common.minutes')}
        </Label>
        <Controller
          control={control}
          name="session_timeout"
          render={({ field }) => (
            <Input
              id="session_timeout"
              type="number"
              placeholder={t('integrations.session_timeout.placeholder')}
              readOnly={!canWrite}
              className={cn(
                'w-full',
                errors.session_timeout && 'border-destructive focus-visible:ring-destructive'
              )}
              {...field}
              // No stored timeout gives NaN minutes, which the input shows as empty
              value={Number.isNaN(field.value) ? '' : (field.value ?? '')}
              onChange={(e) =>
                field.onChange(e.target.value === '' ? null : parseInt(e.target.value, 10))
              }
            />
          )}
        />
        {errors.session_timeout?.message && (
          <p className="text-sm text-destructive">{errors.session_timeout.message}</p>
        )}
      </div>
    </FormFieldset>
  );
};

export default SessionTimeoutFieldset;
